import { Modal } from "react-bootstrap";
import { RiCloseLargeFill } from "react-icons/ri";
import { useState, useContext } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import OutletUsersServices from "../../../services/OutletUsersServices";
import { AuthContext } from "../../../context/AuthContext";

const AddWalletAmountModal = (props) => {
  const {
    walletModal,
    setWalletModal,
    walletUser,
    setWalletUser,
    setOutletUserList,
    adminWalletBalance,
    setAdminWalletBalance,
    role_id,
  } = props;

  const outletSerApi = new OutletUsersServices();
  const { user } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const initialValues = {
    wallet_payment: "Receipt",
    wallet_balance: "",
    remark: "",
    admin_wallet_balance: adminWalletBalance,
  };

  const validationSchema = Yup.object().shape({
    wallet_balance: Yup.number()
      .typeError("Wallet balance must be a number")
      .positive("Wallet balance must be greater than 0")
      .required("Wallet balance is required"),
  });

  const onClose = () => {
    setWalletModal(false);
    setWalletUser(null);
  };

  return (
    <Modal
      show={walletModal}
      onHide={onClose}
      centered
      backdrop="static"
      keyboard={false}
      size="md"
    >
      {/* HEADER */}
      <div className="modal-header border-0 pb-0">
        <h5 className="modal-title">Add Wallet Balance</h5>
        <button className="btn" onClick={onClose}>
          <RiCloseLargeFill size={20} />
        </button>
      </div>
      <hr className="my-1" />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting }) => {          
          if (
            role_id !== 1 && 
            values?.wallet_payment === "Receipt" &&
            Number(values?.wallet_balance) >
              Number(values?.admin_wallet_balance)
          ) {
            toast.error(
              "Please enter amount less than or equal to your wallet balance",
            );
            setIsLoading(false);
            setSubmitting(false);
            return;
          } else if(values?.wallet_payment === "Payment" && Number(values?.wallet_balance) > Number(walletUser?.wallet_balance)){
            toast.error(
              `Please enter amount less than or equal to ${walletUser?.username} wallet balance.`,
            );
            setIsLoading(false);
            setSubmitting(false);
            return;
          }else {
            let finalAmount = 0;
            let type = "CREDIT";
            let remark = "";
            if(values?.wallet_payment === "Payment"){
              type = "DEBIT";
              remark = `Deduct Wallet Balance by ${user?.name}.`;
              finalAmount = (Number(values?.admin_wallet_balance) || 0) +
                (Number(values?.wallet_balance) || 0);
            } else {
              remark = `Add Wallet Balance by ${user?.name}.`;
              finalAmount = (Number(values?.admin_wallet_balance) || 0) -
                (Number(values?.wallet_balance) || 0);
            }
            const payload = {
              user_id: walletUser.id,
              wallet_payment: values?.wallet_payment,
              wallet_balance: values?.wallet_balance,
              type: type,
              remark: remark,
              admin_wallet_balance: finalAmount,
            };

            setIsLoading(true);
            setSubmitting(true);
            outletSerApi
              .addOutletWalletBalanceBySA(payload)
              .then((response) => {
                if (response.isSuccess) {
                  toast.success(response.message);
                  onClose();

                  setOutletUserList((prev) =>
                    prev.map((u) =>
                      u.id === walletUser.id
                        ? { ...u, wallet_balance: response.balance }
                        : u,
                    ),
                  );
                  setAdminWalletBalance(finalAmount);
                } else {
                  toast.error(response.message);
                }
              })
              .catch((error) => {
                console.error("Error addOutletWalletBalanceBySA:", error);
                toast.dismiss();
                toast.error(
                  error?.response?.data?.message ||
                    "Error add outlet wallet balance.",
                );
              })
              .finally(() => {
                setIsLoading(false);
                setSubmitting(false);
              });
          }
        }}
      >
        {({ errors, touched, isSubmitting, values, setFieldValue }) => {
          const finalAmount =
            values?.wallet_payment === "Payment"
              ? (Number(values?.admin_wallet_balance) || 0) +
                (Number(values?.wallet_balance) || 0)
              : (Number(values?.admin_wallet_balance) || 0) -
                (Number(values?.wallet_balance) || 0);

          const userWalletBalance =
            (values?.wallet_payment === "Payment" &&
              (Number(walletUser?.wallet_balance) || 0) -
                (Number(values?.wallet_balance) || 0));
          return (
            <Form className="addAdmin-form">
              <Modal.Body className="pt-2">
                <div className="form-group mb-2">
                  <div className="form-label mb-0">
                    Payment Method <span className="required-asterisk">*</span>
                  </div>
                  <div className="input-wrapper add-wallet-payment-options">
                    <div className="add-wallet-radio-option">
                      <input
                        type="radio"
                        className="form-check-input me-1"
                        name="wallet_payment"
                        id="wallet_payment_receipt"
                        value="Receipt"
                        checked={values.wallet_payment === "Receipt"}
                        onChange={(e) =>
                          setFieldValue("wallet_payment", e.target.value)
                        }
                      />
                      <label htmlFor="wallet_payment_receipt">
                        Receipt (Add Balance)
                      </label>
                    </div>
                    <div className="add-wallet-radio-option">
                      <input
                        type="radio"
                        className="form-check-input me-1"
                        name="wallet_payment"
                        id="wallet_payment_payment"
                        value="Payment"
                        checked={values.wallet_payment === "Payment"}
                        onChange={(e) =>
                          setFieldValue("wallet_payment", e.target.value)
                        }
                      />
                      <label htmlFor="wallet_payment_payment">
                        Payment (Deduct Balance)
                      </label>
                    </div>
                  </div>
                </div>

                <div className="form-group mb-2">
                  <label htmlFor="wallet_balance" className="form-label mb-0">
                    Amount <span className="required-asterisk">*</span>
                  </label>
                  <div className="input-wrapper">
                    <Field
                      type="wallet_balance"
                      id="wallet_balance"
                      name="wallet_balance"
                      placeholder="Enter amount"
                      className={`form-input ${errors.wallet_balance && touched.wallet_balance ? "input-error" : ""}`}
                    />
                  </div>
                  <ErrorMessage name="wallet_balance">
                    {(msg) => <div className="admin_error-text">{msg}</div>}
                  </ErrorMessage>
                </div>
                {/* <div className="form-grou mb-3">
                  <label className="form-label">Remark</label>

                  <Field
                    as="textarea"
                    name="remark"
                    rows={3}
                    placeholder="Enter remark (ex: Wallet credited by admin)"
                    className={`form-input ${errors.remark && touched.remark ? "input-error" : ""}`}
                  />

                  <ErrorMessage name="remark">
                    {(msg) => <div className="error-text">{msg}</div>}
                  </ErrorMessage>
                </div> */}

                <div
                  className={`d-flex align-items-center justify-content-${(role_id === 2 || role_id === 5) ? "between" : "end"}`}
                >
                  <div>
                    {(role_id === 2 || role_id === 5) && (
                      <div
                        className={`amount-text ${finalAmount < 0 ? "loss" : "profit"}`}
                      >
                        After wallet balance:{" "}
                        {finalAmount.toLocaleString("en-IN")}
                      </div>
                    )}
                    {((role_id === 2 || role_id === 5) || role_id === 1) &&
                      values?.wallet_payment === "Payment" && (
                        <div
                          className={`amount-text ${userWalletBalance < 0 ? "loss" : "profit"}`}
                        >
                          {walletUser?.username} wallet balance:{" "}
                          {userWalletBalance.toLocaleString("en-IN")}
                        </div>
                      )}
                  </div>

                  <button
                    type="submit"
                    className="add_admin-btn"
                    disabled={isSubmitting || isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Submitting...
                      </>
                    ) : (
                      values?.wallet_payment === "Payment" ? "Deduct Now" : "Add Now"
                    )}
                  </button>
                </div>
              </Modal.Body>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default AddWalletAmountModal;
